import React, { useEffect, useState } from "react";
import { Modal, Typography, Button } from "antd";
import LineChart, { LineData } from "../../../../components/LineChart";
import {
  ResponseData,
  isGeoLiftResponse,
  isAggregateResponse,
} from "./GeoLiftDataProcessor";
import {
  SafetyOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { DollarSign, TrendingUp } from "lucide-react";

interface MarketDetailsModalProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentMarketData: any;
  modalContent: string;
  dollarPrefix?: string;
}

const { Text } = Typography;

const MarketDetailsModal: React.FC<MarketDetailsModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  currentMarketData,
  modalContent,
  dollarPrefix,
}) => {
  const [chartKey, setChartKey] = useState<number>(Date.now());

  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => setChartKey(Date.now()), 300);
    }
  }, [isModalOpen]);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // Helper function to format market name
  const formatMarketName = (name: string): string => {
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (!currentMarketData) return null;
  console.log(currentMarketData, "currentMarketData");
  const headingDate = Object.values(
    currentMarketData?.lift_plot?.startCampaignDate || {}
  )["0"];
  const verticleLineIndex = Object.values(
    currentMarketData?.lift_plot?.startCampaignIndex || {}
  )["0"];
  console.log(headingDate, "headingDate");
  console.log(verticleLineIndex, "verticleLineIndex");
  let marketData: ResponseData | any;
  // Check if data is in the expected format
  if (currentMarketData.data && typeof currentMarketData.data === "object") {
    // API response structure
    marketData = currentMarketData.data;
  } else {
    // Direct data structure
    marketData = currentMarketData;
  }

  // Check if it's a GeoLiftResponse
  if (isGeoLiftResponse(marketData)) {
    // Create datasets for the chart
    const timeKeys = Object.keys(marketData.lift_plot.Time)
      .map(Number)
      .sort((a, b) => a - b);
    const labels = Object.entries(marketData?.lift_plot?.date)
      .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
      .map(([_, value]) => `${value}`);

    // Create ATT dataset
    const attPlot = marketData.att_plot;
    const estimateData = Object.values(attPlot.Estimate).map((value: number) =>
      parseFloat(value.toFixed(2))
    );
    const attDataSet: LineData[] = [
      {
        data: estimateData,
        borderColor: "rgba(54, 162, 235, 1)",
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        lineType: "dashed",
        name: "Incremental outcome",
        textColor: "rgba(99, 102, 241, 1)", // Indigo / Purple
      },
    ];

    // Create treatment vs control dataset
    const liftPlot = marketData.lift_plot;
    const tObsData = Object.entries(liftPlot.t_obs)
      .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
      .map(([_, value]) => parseFloat(value.toFixed(2)));

    const cObsData = Object.entries(liftPlot.c_obs)
      .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
      .map(([_, value]) => parseFloat(value.toFixed(2)));

    const treatmentControlLabels = Object.entries(liftPlot.date)
      .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
      .map(([_, value]) => `${value}`);

    const treatmentControlDataSets: LineData[] = [
      {
        data: tObsData,
        borderColor: "rgba(135, 206, 250, 1)", // Light Blue border
        backgroundColor: "rgba(135, 206, 250, 0.3)", // Light Blue background
        name: "Test",
        textColor: "blue",
      },
      {
        data: cObsData,
        borderColor: "rgba(128, 128, 128, 1)", // Gray border
        backgroundColor: "rgba(128, 128, 128, 0.2)", // Gray background
        textColor: "rgba(128, 128, 128, 1)",
        name: "Synthetic Control",
      },
    ];

    return (
      <Modal
        title={`Market Details: ${formatMarketName(modalContent)}, Campaign start date : ${headingDate}`}
        open={isModalOpen}
        onCancel={handleModalClose}
        width={"70%"}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
      >
        <div className="space-y-6 w-full max-h-[80vh] overflow-x-hidden overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center mb-2">
                  <TrendingUp className="h-5 w-5 mr-2 text-white/80" />
                  <span className="font-medium text-white/90">Lift Percentage</span>
                </div>
                <div className={`text-2xl font-bold ${
                  marketData.lift_percentage >= 0
                    ? 'text-green-400'
                    : 'text-red-400'
                }`}>
                  {marketData.lift_percentage >= 0 ? '+' : ''}{marketData.lift_percentage.toFixed(2)}%
                </div>
              </div>
            </div>

            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center mb-2">
                  <SafetyOutlined className="text-white/80 mr-2" style={{ fontSize: '20px' }} />
                  <span className="font-medium text-white/90">Confidence</span>
                </div>
                <div className="text-2xl font-bold text-white">
                  {((1 - marketData.pvalue) * 100).toFixed(2)}%
                </div>
              </div>
            </div>

            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <span className="font-medium text-blue-100 mb-2">
                  Test Outcome
                </span>
                <div className="text-2xl font-bold text-white">
                  {dollarPrefix} {Math.round(marketData.salesWithCampaign).toLocaleString()}
                </div>
              </div>
            </div>

            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <span className="font-medium text-blue-100 mb-2">
                  Control Outcome
                </span>
                <div className="text-2xl font-bold text-white">
                  {dollarPrefix} {Math.round(marketData.salesWithoutCampaign).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-3">
              Test vs. Synthetic Control over time
            </h3>
            <div className="">
              <LineChart
                verticalLineAt={verticleLineIndex as number}
                key={`tc-${chartKey}`}
                dataSets={treatmentControlDataSets}
                labels={treatmentControlLabels}
                yAxisTitle="Outcome KPI"
                xAxisTitle="Time"
                yAxisMin={0}
              />
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-medium mb-3">
              Incremental outcome over time
            </h3>
            <div className="h-64">
              <LineChart
                verticalLineAt={verticleLineIndex as number}
                key={`att-${chartKey}`}
                dataSets={attDataSet}
                labels={labels}
                yAxisTitle="Incremental outcome"
                xAxisTitle="Time"
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  } else if (isAggregateResponse(marketData)) {
    // Handle aggregate response
    return (
      <Modal
        title={`Aggregate Data: ${formatMarketName(modalContent)}`}
        open={isModalOpen}
        onCancel={handleModalClose}
        width={800}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center mb-2">
                  <TrendingUp className="h-5 w-5 mr-2 text-white/80" />
                  <span className="font-medium text-white/90">Total Lift</span>
                </div>
                <div className={`text-2xl font-bold ${
                  marketData.lift.totalLiftPercentage >= 0
                    ? 'text-green-400'
                    : 'text-red-400'
                }`}>
                  {marketData.lift.totalLiftPercentage >= 0 ? '+' : ''}{marketData.lift.totalLiftPercentage.toFixed(2)}%
                </div>
              </div>
            </div>
            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center mb-2">
                  <DollarSign className="h-5 w-5 text-white/80 mr-2" />
                  <span className="font-medium text-blue-100">iROAS</span>
                </div>
                <div className="text-2xl font-bold text-white">
                  {marketData.roas.totalROAS.toFixed(2)}x
                </div>
              </div>
            </div>
            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <span className="font-medium text-blue-100 mb-2">
                  Test Outcome
                </span>
                <div className="text-2xl font-bold text-white">
                  {dollarPrefix} {Math.round(marketData.lift.totalSalesWithCampaign).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <span className="font-medium text-blue-100 mb-2">
                  Control Outcome
                </span>
                <div className="text-2xl font-bold text-white">
                  {dollarPrefix} {Math.round(marketData.lift.totalSalesWithoutCampaign).toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {marketData.graph && marketData.graph.salesWithCampaignData && (
            <div className="mt-6">
              <h3 className="text-lg font-medium mb-3">Sales Comparison</h3>
              <div className="h-64">
                <LineChart
                  key={`graph-${chartKey}`}
                  dataSets={[
                    {
                      data: marketData.graph.salesWithCampaignData,
                      borderColor: "rgba(75, 192, 192, 1)",
                      backgroundColor: "rgba(75, 192, 192, 0.2)",
                      name: "With Campaign",
                    },
                    {
                      data: marketData.graph.salesWithoutCampaignData,
                      borderColor: "rgba(255, 99, 132, 1)",
                      backgroundColor: "rgba(255, 99, 132, 0.2)",
                      name: "Without Campaign",
                    },
                  ]}
                  labels={
                    marketData.graph.labels ||
                    Array.from(
                      { length: marketData.graph.salesWithCampaignData.length },
                      (_, i) => `Day ${i + 1}`
                    )
                  }
                  yAxisTitle="Sales"
                  xAxisTitle="Time"
                  yAxisMin={0}
                />
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  return null;
};

export default MarketDetailsModal;
